import React from 'react';
import { motion } from 'framer-motion';
import { useNavigation } from '../NavigationContext';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .ps-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .ps-page { font-family: 'Inter', sans-serif; color: #0c1a36; background: #ffffff; width: 100%; }

  .ps-breadcrumb { max-width: 1200px; margin: 0 auto; padding: 24px 24px 0; display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94a3b8; }
  .ps-breadcrumb-btn { background: none; border: none; cursor: pointer; color: #1e4d8c; font-size: 13px; font-weight: 600; padding: 0; display: inline-flex; align-items: center; gap: 5px; transition: color 0.2s; }
  .ps-breadcrumb-btn:hover { color: #c9931a; }

  /* ── Hero ── */
  .ps-hero { position: relative; padding: 60px 24px 100px; overflow: hidden; background: #fff; }
  .ps-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 540px; height: 540px; border-radius: 50%; background: radial-gradient(circle, rgba(30,77,140,0.26), rgba(201,147,26,0.12)); filter: blur(80px); pointer-events: none; }
  .ps-hero-blob2 { position: absolute; bottom: -12%; left: -6%; width: 580px; height: 580px; border-radius: 50%; background: radial-gradient(circle, rgba(12,26,54,0.18), rgba(30,77,140,0.1)); filter: blur(80px); pointer-events: none; }
  .ps-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .ps-hero-inner { grid-template-columns: 1fr; } .ps-hero-right { display: none; } }

  .ps-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px; border-radius: 999px; background: #fef3c7; border: 1px solid #fde68a; font-size: 12.5px; font-weight: 600; color: #92400e; margin-bottom: 28px; }
  .ps-hero h1 { font-size: clamp(1.725rem, 3.375vw, 2.85rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #0c1a36; margin-bottom: 22px; }
  .ps-gradient { background: linear-gradient(135deg, #1e4d8c 0%, #c9931a 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .ps-hero-sub { font-size: 1.1rem; color: #475569; line-height: 1.82; margin-bottom: 36px; max-width: 520px; }
  .ps-btn-group { display: flex; gap: 14px; flex-wrap: wrap; }
  .ps-btn-primary { padding: 14px 28px; background: #0c1a36; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; }
  .ps-btn-primary:hover { background: #1e4d8c; transform: translateY(-1px); }
  .ps-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .ps-btn-secondary:hover { border-color: #fde68a; background: #fffbeb; color: #92400e; }

  /* Profile completeness card */
  .ps-hero-right { display: flex; flex-direction: column; gap: 14px; }
  .ps-profile-card { background: #0c1a36; border-radius: 20px; padding: 28px 32px; position: relative; overflow: hidden; box-shadow: 0 24px 64px rgba(12,26,54,0.3); }
  .ps-profile-dots { position: absolute; inset: 0; opacity: 0.07; background-image: radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 0); background-size: 20px 20px; pointer-events: none; }
  .ps-profile-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .ps-profile-title { font-size: 13.5px; font-weight: 700; color: #fff; }
  .ps-profile-score { font-size: 22px; font-weight: 900; color: #f59e0b; }
  .ps-profile-field-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.07); }
  .ps-profile-field-row:last-child { border-bottom: none; }
  .ps-field-status { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; }
  .ps-field-status.done { background: rgba(16,185,129,0.2); color: #34d399; }
  .ps-field-status.warn { background: rgba(245,158,11,0.2); color: #fbbf24; }
  .ps-field-status.err { background: rgba(220,38,38,0.2); color: #f87171; }
  .ps-field-label { font-size: 12.5px; color: rgba(255,255,255,0.7); flex: 1; }
  .ps-field-badge { font-size: 10.5px; font-weight: 600; padding: 3px 9px; border-radius: 999px; }
  .ps-field-badge.complete { background: rgba(16,185,129,0.15); color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
  .ps-field-badge.needed { background: rgba(245,158,11,0.15); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
  .ps-field-badge.missing { background: rgba(220,38,38,0.15); color: #f87171; border: 1px solid rgba(220,38,38,0.25); }
  .ps-mini-card { background: #fff; border-radius: 16px; padding: 18px 22px; display: flex; align-items: center; gap: 14px; box-shadow: 0 4px 16px rgba(12,26,54,0.1); }
  .ps-mini-icon { width: 40px; height: 40px; border-radius: 10px; background: #fef3c7; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .ps-mini-label { font-size: 11px; color: #94a3b8; font-weight: 500; }
  .ps-mini-value { font-size: 15px; font-weight: 800; color: #0c1a36; margin-top: 2px; }

  /* ── Shared ── */
  .ps-section { padding: 88px 24px; }
  .ps-container { max-width: 1200px; margin: 0 auto; }
  .ps-section-header { text-align: center; max-width: 720px; margin: 0 auto 60px; }
  .ps-section-header h2 { font-size: clamp(1.8rem, 3.2vw, 2.7rem); font-weight: 800; color: #0c1a36; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.2; }
  .ps-section-header p { font-size: 1.03rem; color: #64748b; line-height: 1.8; }
  .ps-kicker { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #1e4d8c; margin-bottom: 12px; }

  /* ── What a Profile Includes ── */
  .ps-includes-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.7); }
  .ps-includes-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 900px) { .ps-includes-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 580px) { .ps-includes-grid { grid-template-columns: 1fr; } }
  .ps-inc-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; padding: 26px 22px; transition: all 0.3s; }
  .ps-inc-card:hover { box-shadow: 0 12px 36px rgba(0,0,0,0.07); transform: translateY(-2px); }
  .ps-inc-icon { font-size: 26px; margin-bottom: 14px; }
  .ps-inc-card h4 { font-size: 0.97rem; font-weight: 700; color: #0c1a36; margin-bottom: 8px; }
  .ps-inc-card p { font-size: 0.87rem; color: #64748b; line-height: 1.68; }

  /* ── Why Completeness Matters (dark) ── */
  .ps-why-bg { background: #0c1a36; position: relative; overflow: hidden; }
  .ps-why-glow { position: absolute; top: -8%; right: 15%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(201,147,26,0.14), transparent 70%); pointer-events: none; }
  .ps-why-bg .ps-kicker { color: #fbbf24; }
  .ps-why-bg .ps-section-header h2 { color: #fff; }
  .ps-why-bg .ps-section-header p { color: #94a3b8; }
  .ps-why-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  @media (max-width: 700px) { .ps-why-grid { grid-template-columns: 1fr; } }
  .ps-why-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 28px; transition: all 0.3s; }
  .ps-why-card:hover { border-color: rgba(201,147,26,0.35); background: rgba(255,255,255,0.07); }
  .ps-why-card-icon { font-size: 26px; margin-bottom: 14px; }
  .ps-why-card h4 { font-size: 0.97rem; font-weight: 700; color: #fff; margin-bottom: 8px; }
  .ps-why-card p { font-size: 0.87rem; color: #94a3b8; line-height: 1.68; }

  /* ── What We Configure ── */
  .ps-config-bg { background: #fff; }
  .ps-config-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
  @media (max-width: 900px) { .ps-config-layout { grid-template-columns: 1fr; } }
  .ps-config-left h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800; color: #0c1a36; letter-spacing: -0.02em; line-height: 1.2; margin-bottom: 16px; }
  .ps-config-left p { font-size: 1rem; color: #475569; line-height: 1.82; margin-bottom: 32px; }
  .ps-config-list { display: flex; flex-direction: column; gap: 12px; }
  .ps-config-item { display: flex; align-items: flex-start; gap: 12px; padding: 16px 18px; background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 14px; transition: all 0.2s; }
  .ps-config-item:hover { background: #fff; border-color: #e2e8f0; box-shadow: 0 4px 16px rgba(0,0,0,0.05); }
  .ps-config-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
  .ps-config-item h5 { font-size: 0.92rem; font-weight: 700; color: #0c1a36; margin-bottom: 3px; }
  .ps-config-item p { font-size: 0.83rem; color: #64748b; line-height: 1.6; }
  .ps-naics-card { background: linear-gradient(150deg, #0c1a36, #1e3a6e); border-radius: 20px; padding: 32px; color: #fff; }
  .ps-naics-title { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.5); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 20px; }
  .ps-naics-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
  .ps-naics-row:last-child { border-bottom: none; }
  .ps-naics-code { font-size: 13px; font-weight: 700; color: #f59e0b; width: 56px; flex-shrink: 0; font-family: monospace; }
  .ps-naics-label { font-size: 12.5px; color: rgba(255,255,255,0.7); flex: 1; }
  .ps-naics-badge { font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; background: rgba(16,185,129,0.15); color: #34d399; border: 1px solid rgba(16,185,129,0.25); }

  /* ── CTA ── */
  .ps-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .ps-cta-inner { background: linear-gradient(135deg, #0c1a36 0%, #1a3568 60%, #0c1a36 100%); border-radius: 28px; padding: 80px 56px; text-align: center; position: relative; overflow: hidden; max-width: 900px; margin: 0 auto; }
  @media (max-width: 640px) { .ps-cta-inner { padding: 52px 28px; border-radius: 20px; } }
  .ps-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(201,147,26,0.22), transparent 65%); pointer-events: none; }
  .ps-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(30,77,140,0.3), transparent 65%); pointer-events: none; }
  .ps-cta-inner h2 { font-size: clamp(1.9rem, 3.5vw, 2.8rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 16px; position: relative; z-index: 1; }
  .ps-cta-inner > p { font-size: 1.05rem; color: #94a3b8; max-width: 500px; margin: 0 auto 36px; line-height: 1.75; position: relative; z-index: 1; }
  .ps-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .ps-cta-primary { padding: 14px 30px; background: #c9931a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 18px rgba(201,147,26,0.4); }
  .ps-cta-primary:hover { background: #e0a820; transform: translateY(-1px); }
  .ps-cta-secondary { padding: 14px 30px; background: rgba(255,255,255,0.07); color: #cbd5e1; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .ps-cta-secondary:hover { background: rgba(255,255,255,0.13); color: #fff; }
`;

const fadeUp = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.55 } };
const stagger = (i: number) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.09 } });

const includesItems = [
  { icon: '🪪', title: 'UEI & CAGE Code', desc: 'Your Unique Entity Identifier and Commercial and Government Entity code — the core identifiers for every federal interaction.' },
  { icon: '🏢', title: 'Entity Information', desc: 'Legal business name, DBA, address, business type, organizational structure, and point of contact details.' },
  { icon: '📊', title: 'NAICS Codes', desc: 'North American Industry Classification System codes that define your business capabilities and determine which solicitations you appear in.' },
  { icon: '🗂️', title: 'PSC Codes', desc: 'Product and Service Codes that classify the specific products, services, and R&D your business provides to federal agencies.' },
  { icon: '💰', title: 'Financial Information', desc: 'Annual revenue, number of employees, fiscal year end date, and electronic funds transfer (EFT) banking details for payment.' },
  { icon: '📋', title: 'Reps & Certifications', desc: 'Representations and certifications including small business status, socioeconomic programs, compliance attestations, and FAR clauses.' },
];

const whyCards = [
  { icon: '💳', title: 'Incomplete Profiles Block Payment', desc: 'Federal agencies cannot process payments to entities with incomplete or unverified SAM.gov profiles. Missing EFT information alone stops all payments.' },
  { icon: '🔍', title: 'Wrong Codes = Missed Opportunities', desc: 'Incorrect NAICS or PSC codes mean your business is invisible in agency procurement searches. You simply will not appear in the right solicitations.' },
  { icon: '🚫', title: 'Errors Trigger Compliance Flags', desc: 'Data mismatches between your profile and IRS or CAGE records trigger automated compliance flags that can delay awards and require manual resolution.' },
  { icon: '📅', title: 'Outdated Info Disqualifies Bids', desc: 'Point of contact information, addresses, and certifications must be current. Contracting officers reject bids from entities with outdated SAM.gov records.' },
];

const configItems = [
  { icon: '🪪', title: 'UEI & CAGE Code Linkage', desc: 'We ensure your UEI and CAGE code are correctly cross-referenced and validated.' },
  { icon: '🏢', title: 'Entity & Legal Name Setup', desc: 'Exact match against IRS records to prevent validation failures and rejection.' },
  { icon: '📊', title: 'NAICS Code Selection', desc: 'Research-backed primary and secondary codes aligned to your capabilities.' },
  { icon: '🗂️', title: 'PSC Code Assignment', desc: 'Product and service codes matched to your offerings for maximum search visibility.' },
  { icon: '💳', title: 'EFT / Banking Information', desc: 'Electronic funds transfer details set up correctly for seamless federal payments.' },
  { icon: '📋', title: 'Reps & Certifications', desc: 'All FAR clause responses and small business representations completed accurately.' },
  { icon: '👤', title: 'Points of Contact', desc: 'Government business POC and electronic business POC configured and verified.' },
  { icon: '✅', title: 'Final Review & Activation', desc: 'End-to-end audit before submission to catch errors before they cause rejection.' },
];

const SAMGovProfileSetup: React.FC = () => {
  const { navigateTo } = useNavigation();

  return (
    <>
      <style>{styles}</style>
      <div className="ps-page">

        <div className="ps-breadcrumb">
          <button className="ps-breadcrumb-btn" onClick={() => navigateTo('')}>← SAM.gov Registration</button>
          <span>/</span>
          <span>Profile Setup</span>
        </div>

        {/* Hero */}
        <section className="ps-hero">
          <div className="ps-hero-blob1" />
          <div className="ps-hero-blob2" />
          <div className="ps-hero-inner">
            <motion.div {...fadeUp}>
              <div className="ps-badge">🏛️ Step 2 of Federal Registration</div>
              <h1>
                Your Complete SAM.gov Profile,{' '}
                <span className="ps-gradient">Built Without Errors</span>
              </h1>
              <p className="ps-hero-sub">
                A SAM.gov profile is more than filling out a form. It requires precise alignment
                of your CAGE code, NAICS codes, PSC codes, financial data, and representations
                with federal records. One error blocks payments and disqualifies bids.
              </p>
              <div className="ps-btn-group">
                <button className="ps-btn-primary">Set Up My Profile →</button>
                <button className="ps-btn-secondary">Talk to a Specialist</button>
              </div>
            </motion.div>

            <motion.div className="ps-hero-right" initial={{ opacity: 0, x: 36 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, delay: 0.2 }}>
              <div className="ps-profile-card">
                <div className="ps-profile-dots" />
                <div className="ps-profile-header">
                  <span className="ps-profile-title">Profile Completeness Score</span>
                  <span className="ps-profile-score">100%</span>
                </div>
                {[
                  { status: 'done', label: 'UEI & CAGE Code', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'Legal Entity Name (IRS Matched)', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'NAICS Codes (Primary + Secondary)', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'PSC Codes', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'EFT Banking Information', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'Reps & Certifications', badge: 'Complete', cls: 'complete' },
                  { status: 'done', label: 'Points of Contact', badge: 'Complete', cls: 'complete' },
                ].map((r, i) => (
                  <div key={i} className="ps-profile-field-row">
                    <div className={`ps-field-status ${r.status}`}>✓</div>
                    <span className="ps-field-label">{r.label}</span>
                    <span className={`ps-field-badge ${r.cls}`}>{r.badge}</span>
                  </div>
                ))}
              </div>
              <div className="ps-mini-card">
                <div className="ps-mini-icon">⚡</div>
                <div>
                  <div className="ps-mini-label">Average Setup Time</div>
                  <div className="ps-mini-value">2–3 Business Days</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What a Profile Includes */}
        <section className="ps-section ps-includes-bg">
          <div className="ps-container">
            <motion.div className="ps-section-header" {...fadeUp}>
              <div className="ps-kicker">Profile Components</div>
              <h2>Everything That Goes Into a Complete SAM.gov Profile</h2>
              <p>A SAM.gov profile has six major data sections. All of them must be accurate, current, and internally consistent for your registration to be approved and maintained.</p>
            </motion.div>
            <div className="ps-includes-grid">
              {includesItems.map((item, i) => (
                <motion.div key={i} className="ps-inc-card" {...stagger(i)}>
                  <div className="ps-inc-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Completeness Matters */}
        <section className="ps-section ps-why-bg">
          <div className="ps-why-glow" />
          <div className="ps-container">
            <motion.div className="ps-section-header" {...fadeUp}>
              <div className="ps-kicker">Why It Matters</div>
              <h2>An Incomplete Profile Has Real Consequences</h2>
              <p>Most SAM.gov issues that block contracts and payments trace back to errors or gaps in the profile setup. Here is what goes wrong.</p>
            </motion.div>
            <div className="ps-why-grid">
              {whyCards.map((c, i) => (
                <motion.div key={i} className="ps-why-card" {...stagger(i)}>
                  <div className="ps-why-card-icon">{c.icon}</div>
                  <h4>{c.title}</h4>
                  <p>{c.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Configure */}
        <section className="ps-section ps-config-bg">
          <div className="ps-container">
            <div className="ps-config-layout">
              <motion.div className="ps-config-left" {...fadeUp}>
                <div className="ps-kicker">What We Do</div>
                <h2>Every Field Configured — Every Code Verified</h2>
                <p>
                  Our specialists configure all eight sections of your SAM.gov profile, cross-referencing
                  every entry against IRS, CAGE, and federal records before submission. Nothing is
                  guessed. Nothing is skipped.
                </p>
                <div className="ps-config-list">
                  {configItems.map((item, i) => (
                    <div key={i} className="ps-config-item">
                      <span className="ps-config-icon">{item.icon}</span>
                      <div>
                        <h5>{item.title}</h5>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div {...stagger(1)}>
                <div className="ps-kicker">Example NAICS Code Selection</div>
                <div className="ps-naics-card">
                  <div className="ps-naics-title">Assigned NAICS Codes</div>
                  {[
                    { code: '541512', label: 'Computer Systems Design Services', badge: 'Primary' },
                    { code: '541511', label: 'Custom Computer Programming', badge: 'Secondary' },
                    { code: '541519', label: 'Other Computer Related Services', badge: 'Secondary' },
                    { code: '541690', label: 'Other Scientific & Technical Consulting', badge: 'Secondary' },
                    { code: '518210', label: 'Data Processing & Hosting Services', badge: 'Secondary' },
                  ].map((row, i) => (
                    <div key={i} className="ps-naics-row">
                      <span className="ps-naics-code">{row.code}</span>
                      <span className="ps-naics-label">{row.label}</span>
                      <span className="ps-naics-badge">{row.badge}</span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px', background: '#f8fafc', borderRadius: '16px', padding: '22px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Why Code Selection Matters</div>
                  <p style={{ fontSize: '0.87rem', color: '#475569', lineHeight: 1.72 }}>
                    Federal procurement officers search SAM.gov by NAICS and PSC codes. Businesses with the wrong codes are simply invisible to the agencies most likely to award them contracts.
                    We research which codes generate the highest contract volume for your capabilities.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="ps-section ps-cta-bg">
          <div className="ps-container">
            <motion.div className="ps-cta-inner" {...fadeUp}>
              <div className="ps-cta-glow1" />
              <div className="ps-cta-glow2" />
              <h2>Ready to Build Your Complete SAM.gov Profile?</h2>
              <p>Let our specialists configure every field correctly — the first time. Active and fully searchable in 2–3 business days.</p>
              <div className="ps-cta-btns">
                <button className="ps-cta-primary">Set Up My Profile →</button>
                <button className="ps-cta-secondary">Schedule a Free Consultation</button>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default SAMGovProfileSetup;
